import { useState } from "react";
import type { Company } from "@workspace/api-client-react";
import { useUpdateCompany } from "@workspace/api-client-react";
import { PLANS, ALL_FEATURE_KEYS, FEATURE_LABELS, getPlan, type PlanFeatureKey } from "@/lib/plans";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  X, CheckCircle2, CreditCard, Tag, Calendar,
  MessageSquare, Zap, Package, Star, AlertTriangle,
  ToggleRight, ToggleLeft, Clock, BadgePercent, RefreshCw, Sliders
} from "lucide-react";

function PlanIcon({ id, cls }: { id: string; cls: string }) {
  if (id === "lite")     return <Package className={cls} />;
  if (id === "lite-pro") return <Zap className={cls} />;
  if (id === "pro")      return <Star className={cls} />;
  return <Star className={cls} />;
}

function fmtNum(v: string | number | null | undefined): number | null {
  if (v == null || v === "") return null;
  const n = typeof v === "string" ? parseFloat(v) : v;
  return isNaN(n) ? null : n;
}

export default function SubscriptionEditor({
  company,
  onClose,
}: {
  company: Company;
  onClose: () => void;
}) {
  const updateCompany = useUpdateCompany();

  const [plan,         setPlan]         = useState(company.plan || "pro");
  const [status,       setStatus]       = useState(company.status || "Active");
  const [billingCycle, setBillingCycle] = useState(company.billingCycle || "monthly");
  const [customPrice,  setCustomPrice]  = useState(company.customPrice ?? "");
  const [discountPct,  setDiscountPct]  = useState(String(company.discountPct ?? 0));
  const [discountNote, setDiscountNote] = useState(company.discountNote ?? "");
  const [subStart,     setSubStart]     = useState(company.subscriptionStartAt ? company.subscriptionStartAt.slice(0, 10) : "");
  const [subEnds,      setSubEnds]      = useState(company.subscriptionEndsAt  ? company.subscriptionEndsAt.slice(0, 10)  : "");
  const [trialEnds,    setTrialEnds]    = useState(company.trialEndsAt         ? company.trialEndsAt.slice(0, 10)         : "");
  const [billingNotes, setBillingNotes] = useState(company.billingNotes ?? "");
  const [saving,       setSaving]       = useState(false);
  const [saved,        setSaved]        = useState(false);

  // Per-company feature overrides — starts from the company's stored overrides
  const rawOverrides = (company as any).featureOverrides as Partial<Record<PlanFeatureKey, boolean>> | null | undefined;
  const [featureOverrides, setFeatureOverrides] = useState<Partial<Record<PlanFeatureKey, boolean>>>(rawOverrides ?? {});
  const [overridesEnabled, setOverridesEnabled] = useState(Object.keys(rawOverrides ?? {}).length > 0);

  const planDef = getPlan(plan);
  const discNum = parseInt(discountPct, 10) || 0;
  const customPriceNum = fmtNum(customPrice);
  const isAnnual = billingCycle === "annual";
  const standardBase = isAnnual
    ? (planDef.priceYearly > 0 ? planDef.priceYearly : planDef.priceMonthly * 12)
    : planDef.priceMonthly;
  const basePrice = customPriceNum ?? standardBase;
  const effectivePriceNum = isNaN(basePrice) || basePrice === 0 ? null : basePrice * (1 - discNum / 100);
  const hasCustomBilling = customPriceNum != null || discNum > 0;

  const toggleFeature = (key: PlanFeatureKey) => {
    setFeatureOverrides(prev => {
      const current = key in prev ? prev[key] : planDef.features[key];
      return { ...prev, [key]: !current };
    });
  };

  const getEffectiveFeature = (key: PlanFeatureKey): boolean => {
    if (!overridesEnabled) return planDef.features[key];
    return key in featureOverrides ? !!featureOverrides[key] : planDef.features[key];
  };

  const resetOverrides = () => setFeatureOverrides({});

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateCompany.mutateAsync({
        id: company.id,
        data: {
          plan,
          status,
          billingCycle,
          customPrice:         customPrice === "" ? null : String(customPrice),
          discountPct:         discNum,
          discountNote,
          subscriptionStartAt: subStart || null,
          subscriptionEndsAt:  subEnds  || null,
          trialEndsAt:         trialEnds || null,
          billingNotes,
          featureOverrides:    overridesEnabled && Object.keys(featureOverrides).length > 0 ? featureOverrides : null,
        },
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 w-[500px] bg-white border-l border-gray-200 z-50 flex flex-col shadow-2xl animate-in slide-in-from-right duration-300 overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-sm font-bold shrink-0"
              style={{ backgroundColor: company.color || "#3b82f6" }}
            >
              {company.name[0]}
            </div>
            <div>
              <div className="font-semibold text-gray-900 text-sm">{company.name}</div>
              <div className="text-xs text-gray-400">{company.country} · {company.currency} · {company.id}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 transition-colors p-1 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-7">

          {/* PLAN */}
          <section className="space-y-3">
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
              <CreditCard className="w-3.5 h-3.5" /> Plan
            </div>
            <div className="grid grid-cols-4 gap-2">
              {PLANS.map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => { setPlan(p.id); setFeatureOverrides({}); }}
                  className={`rounded-xl p-2.5 text-left border transition-all ${
                    plan === p.id
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 bg-gray-50 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center gap-1 mb-1">
                    <div className={`w-4 h-4 rounded-md ${p.color} flex items-center justify-center shrink-0`}>
                      <PlanIcon id={p.id} cls="w-2.5 h-2.5 text-white" />
                    </div>
                    <span className={`text-xs font-semibold truncate ${plan === p.id ? "text-blue-700" : "text-gray-700"}`}>{p.short}</span>
                  </div>
                  <div className="text-[10px] text-gray-400 leading-tight">{p.price}</div>
                </button>
              ))}
            </div>
            <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
              <div className="text-xs font-medium text-gray-700 mb-2">{planDef.label} — included</div>
              <div className="grid grid-cols-2 gap-1">
                {planDef.highlights.map(h => (
                  <div key={h} className="flex items-center gap-1.5 text-xs text-gray-600">
                    <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" /> {h}
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-4 mt-2 pt-2 border-t border-gray-200 text-xs text-gray-400">
                <span>{planDef.maxProjects === -1 ? "Unlimited" : planDef.maxProjects} projects</span>
                <span>{planDef.maxUsers === -1 ? "Unlimited" : planDef.maxUsers} users</span>
              </div>
            </div>
          </section>

          {/* STATUS */}
          <section className="space-y-3">
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
              <ToggleRight className="w-3.5 h-3.5" /> Account Status
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "Active",    icon: ToggleRight, activeCls: "border-emerald-500 bg-emerald-50 text-emerald-700" },
                { v: "Trial",     icon: Clock,       activeCls: "border-amber-500 bg-amber-50 text-amber-700" },
                { v: "Suspended", icon: ToggleLeft,  activeCls: "border-rose-500 bg-rose-50 text-rose-700" },
              ].map(s => (
                <button
                  key={s.v}
                  type="button"
                  onClick={() => setStatus(s.v)}
                  className={`rounded-xl p-3 flex items-center gap-2 border transition-all text-sm font-medium ${
                    status === s.v
                      ? s.activeCls
                      : "border-gray-200 bg-gray-50 text-gray-500 hover:border-gray-300"
                  }`}
                >
                  <s.icon className="w-4 h-4" /> {s.v}
                </button>
              ))}
            </div>
            {status === "Trial" && (
              <div className="space-y-1.5">
                <Label className="text-xs text-gray-500">Trial ends on</Label>
                <Input
                  type="date"
                  value={trialEnds}
                  onChange={e => setTrialEnds(e.target.value)}
                  className="rounded-xl h-9 text-sm"
                />
              </div>
            )}
          </section>

          {/* PRICING & DISCOUNT */}
          <section className="space-y-3">
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
              <BadgePercent className="w-3.5 h-3.5" /> Custom Pricing &amp; Discount
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-gray-500">Custom price ({isAnnual ? "yearly" : "monthly"}) ($)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder={isAnnual
                    ? `Standard: $${planDef.priceYearly > 0 ? planDef.priceYearly : (planDef.priceMonthly * 12).toFixed(0)}/yr`
                    : `Standard: $${planDef.priceMonthly}/mo`}
                  value={customPrice}
                  onChange={e => setCustomPrice(e.target.value)}
                  className="rounded-xl h-9 text-sm"
                />
                <p className="text-xs text-gray-400">Blank = use standard price</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-gray-500">Discount %</Label>
                <div className="relative">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={discountPct}
                    onChange={e => setDiscountPct(e.target.value)}
                    className="rounded-xl h-9 text-sm pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                </div>
              </div>
            </div>

            {hasCustomBilling && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-start gap-3">
                <Tag className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <div className="text-xs">
                  <div className="text-gray-600">
                    {customPriceNum != null
                      ? `Custom $${customPriceNum.toFixed(2)}/${isAnnual ? "yr" : "mo"}`
                      : isAnnual
                        ? `Standard $${planDef.priceYearly > 0 ? planDef.priceYearly : (planDef.priceMonthly * 12).toFixed(0)}/yr`
                        : `Standard $${planDef.priceMonthly}/mo`}
                    {discNum > 0 && <span className="ml-1.5 text-rose-600">– {discNum}% discount</span>}
                  </div>
                  <div className="text-emerald-700 font-bold mt-1">
                    Effective:{" "}
                    {effectivePriceNum != null
                      ? isAnnual
                        ? `$${effectivePriceNum.toFixed(2)}/yr ($${(effectivePriceNum / 12).toFixed(2)}/mo)`
                        : `$${effectivePriceNum.toFixed(2)}/mo`
                      : "—"}
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500">Promo / discount reason (internal)</Label>
              <Input
                placeholder="e.g. 3-month early adopter deal, Referral from Ahmed..."
                value={discountNote}
                onChange={e => setDiscountNote(e.target.value)}
                className="rounded-xl h-9 text-sm"
              />
            </div>
          </section>

          {/* SUBSCRIPTION PERIOD */}
          <section className="space-y-3">
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
              <Calendar className="w-3.5 h-3.5" /> Subscription Period
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-gray-500">Billing cycle</Label>
                <Select value={billingCycle} onValueChange={setBillingCycle}>
                  <SelectTrigger className="rounded-xl h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-gray-500">Started on</Label>
                <Input
                  type="date"
                  value={subStart}
                  onChange={e => setSubStart(e.target.value)}
                  className="rounded-xl h-9 text-sm"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500">Renews / expires on</Label>
              <Input
                type="date"
                value={subEnds}
                onChange={e => setSubEnds(e.target.value)}
                className="rounded-xl h-9 text-sm"
              />
              {subEnds && new Date(subEnds) < new Date() && (
                <div className="flex items-center gap-1.5 text-xs text-rose-600 mt-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> This subscription has expired.
                </div>
              )}
              {subEnds && new Date(subEnds) >= new Date() && (
                <div className="text-xs text-gray-400 mt-1">
                  {Math.ceil((new Date(subEnds).getTime() - Date.now()) / 86400000)} days remaining
                </div>
              )}
            </div>
          </section>

          {/* FEATURE OVERRIDES */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <Sliders className="w-3.5 h-3.5" /> Feature Overrides
              </div>
              <div className="flex items-center gap-2">
                {overridesEnabled && (
                  <button
                    type="button"
                    onClick={resetOverrides}
                    className="text-xs text-gray-400 hover:text-gray-700 underline"
                  >
                    Reset to plan defaults
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { setOverridesEnabled(v => !v); if (overridesEnabled) setFeatureOverrides({}); }}
                  className={`relative inline-flex h-5 w-9 rounded-full transition-colors ${overridesEnabled ? "bg-blue-600" : "bg-gray-200"}`}
                >
                  <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform mt-0.5 ${overridesEnabled ? "translate-x-4.5" : "translate-x-0.5"}`} />
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-400">
              {overridesEnabled
                ? "Custom feature access for this company — overrides the plan defaults."
                : "Enable to grant or restrict specific features for this company."}
            </p>
            {overridesEnabled && (
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                {ALL_FEATURE_KEYS.map((key, i) => {
                  const effective = getEffectiveFeature(key);
                  const isOverridden = key in featureOverrides;
                  const planDefault = planDef.features[key];
                  return (
                    <div
                      key={key}
                      className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${i > 0 ? "border-t border-gray-100" : ""}`}
                      onClick={() => toggleFeature(key)}
                    >
                      <div>
                        <div className={`text-sm font-medium ${effective ? "text-gray-900" : "text-gray-400"}`}>
                          {FEATURE_LABELS[key]}
                        </div>
                        {isOverridden && (
                          <div className="text-xs text-amber-600 mt-0.5">
                            Overriding plan default ({planDefault ? "enabled" : "disabled"})
                          </div>
                        )}
                      </div>
                      <div className={`relative inline-flex h-5 w-9 rounded-full transition-colors shrink-0 ml-4 ${effective ? "bg-blue-600" : "bg-gray-200"}`}>
                        <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform mt-0.5 ${effective ? "translate-x-4.5" : "translate-x-0.5"}`} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* INTERNAL NOTES */}
          <section className="space-y-3">
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
              <MessageSquare className="w-3.5 h-3.5" /> Internal Notes
            </div>
            <Textarea
              value={billingNotes}
              onChange={e => setBillingNotes(e.target.value)}
              placeholder="Payment arrangements, special terms, account manager contacts, any notes..."
              rows={4}
              className="rounded-xl resize-none text-sm"
            />
          </section>
        </div>

        {/* Footer */}
        <div className="shrink-0 px-6 py-4 border-t border-gray-200 flex gap-3">
          <Button
            variant="outline"
            className="flex-1 rounded-xl"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            className={`flex-1 rounded-xl text-white transition-all ${
              saved
                ? "bg-emerald-600 hover:bg-emerald-700"
                : "bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-500/20"
            }`}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
            ) : saved ? (
              <><CheckCircle2 className="w-4 h-4 mr-2" /> Saved!</>
            ) : (
              "Save Changes"
            )}
          </Button>
        </div>
      </div>
    </>
  );
}
